import { useState, useEffect, useRef } from 'react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import SuperadminLayout from '../../components/SuperadminLayout';
import { RefreshCw, CircleCheck, TriangleAlert, MoonStar, Archive, Database, ImageIcon, Loader2, Terminal, Plus, CloudUpload } from 'lucide-react';

// Superadmin → Gecə Backup-ları: hər gecə 03:00 (Bakı) alınan tam backup-lar
// (bütün tenantlar: DB + şəkillər). Siyahı, endirmə, «İndi al» (server + Google Drive) və Drive statusu.
const fmtSize = (b) => {
    if (b == null) return '-';
    const mb = b / 1024 / 1024;
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;
};

export default function NightlyBackups() {
    const [data, setData]     = useState(null);
    const [loading, setLoad]  = useState(true);
    const [dling, setDling]   = useState('');   // endirilən fayl adı

    const [starting, setStarting] = useState(false);
    const wasRunning = useRef(false);

    const load = (quiet) => {
        if (!quiet) setLoad(true);
        return api.get('/superadmin/nightly-backups')
            .then(r => setData(r.data))
            .catch(() => { if (!quiet) toast.error('Siyahı alınmadı.'); })
            .finally(() => setLoad(false));
    };
    useEffect(() => { load(); }, []);

    // «İndi al» gedirsə hər 4 saniyədən bir yoxla; bitəndə nəticəni bildir
    const running = !!data?.running;
    useEffect(() => {
        if (running) { wasRunning.current = true; const t = setTimeout(() => load(true), 4000); return () => clearTimeout(t); }
        if (wasRunning.current && data) {
            wasRunning.current = false;
            const g = data.gdrive;
            if (g && g.state === 'OK') toast.success('Backup alındı: serverə və Google Drive-a yazıldı', { duration: 7000 });
            else toast(`Backup serverə yazıldı. Google Drive: ${g ? g.state : 'status yoxdur'}`, { icon: '⚠️', duration: 9000 });
        }
        return undefined;
    }, [running, data]); // eslint-disable-line react-hooks/exhaustive-deps

    const runNow = async () => {
        setStarting(true);
        try {
            const r = await api.post('/superadmin/nightly-backups/run');
            if (!r.data.ok) { toast.error(r.data.msg || 'Başladılmadı'); return; }
            toast('Backup alınır. 1-2 dəqiqə çəkə bilər.', { icon: '⏳' });
            await load(true);
        } catch { toast.error('Başladılmadı'); }
        finally { setStarting(false); }
    };

    const download = async (name) => {
        setDling(name);
        const t = toast.loading('Endirilir…');
        try {
            const res = await api.get('/superadmin/nightly-backups/file', {
                params: { name }, responseType: 'blob', timeout: 600000,
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url; a.download = name;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Endirildi', { id: t });
        } catch (err) {
            toast.error('Endirilmədi: ' + (err.response?.data?.msg || err.message), { id: t });
        } finally {
            setDling('');
        }
    };

    const list = data?.list || [];
    const last = list[0];

    return (
        <SuperadminLayout
            title="Gecə backup-ları"
            subtitle="Hər gecə 03:00 (Bakı). Bütün tenantların DB + şəkilləri"
            actions={
                <>
                    <button onClick={() => load()} className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50">
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Yenilə
                    </button>
                    <button onClick={runNow} disabled={running || starting}
                        className="flex items-center gap-1.5 rounded-xl bg-[#0090bb] px-3.5 py-2 text-xs font-semibold text-white shadow-sm shadow-sky-900/20 transition-colors hover:bg-[#007ea4] disabled:opacity-60">
                        {running || starting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                        {running ? 'Alınır…' : 'İndi al'}
                    </button>
                </>
            }
        >
            {/* Status kartları */}
            {running && (
                <div className="mb-5 flex items-center gap-3 rounded-2xl bg-sky-50 px-4 py-3 text-sm text-sky-900 ring-1 ring-sky-200">
                    <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-[#0090bb]" />
                    Backup alınır: əvvəl serverə, sonra şifrəli nüsxə Google Drive-a. 1-2 dəqiqə çəkə bilər, səhifəni bağlaya bilərsiniz.
                </div>
            )}
            <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <div className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                    <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${last ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'}`}>
                        {last ? <CircleCheck className="h-5 w-5" /> : <TriangleAlert className="h-5 w-5" />}
                    </div>
                    <div className="min-w-0">
                        <div className="text-xs text-slate-500">Son backup</div>
                        <div className="truncate text-sm font-semibold text-slate-900">{loading ? '…' : (last ? last.date : 'Tapılmadı')}</div>
                    </div>
                </div>
                {(() => {
                    const g = data?.gdrive;
                    const okG = g && g.state === 'OK';
                    const tone = !g ? 'bg-slate-100 text-slate-400' : okG ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600';
                    const text = !g ? 'Status yoxdur' : okG ? `Göndərildi · ${g.at}` : g.state === 'SKIP' ? 'Qurulmayıb' : `Alınmadı · ${g.at}`;
                    return (
                        <div className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                            <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${tone}`}>
                                <CloudUpload className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-xs text-slate-500">Google Drive (şifrəli)</div>
                                <div className="truncate text-sm font-semibold text-slate-900">{loading && !data ? '…' : text}</div>
                            </div>
                        </div>
                    );
                })()}
                <div className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-500">
                        <MoonStar className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-xs text-slate-500">Cədvəl</div>
                        <div className="truncate text-sm font-semibold text-slate-900">Hər gecə 03:00 (Bakı)</div>
                    </div>
                </div>
                <div className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                        <Archive className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-xs text-slate-500">Saxlanma</div>
                        <div className="truncate text-sm font-semibold text-slate-900">Server 30 gün · Drive 14 gün</div>
                    </div>
                </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                            <th className="px-5 py-3">Tarix</th>
                            <th className="px-5 py-3">Baza (DB)</th>
                            <th className="px-5 py-3">Şəkillər</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={3} className="px-5 py-10 text-center text-slate-400">Yüklənir…</td></tr>
                        ) : list.length === 0 ? (
                            <tr><td colSpan={3} className="px-5 py-10 text-center text-slate-400">Hələ backup yoxdur.</td></tr>
                        ) : list.map((b, i) => (
                            <tr key={b.ts} className="border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50/60">
                                <td className="whitespace-nowrap px-5 py-3">
                                    <div className="flex items-center gap-2 font-medium text-slate-800">
                                        {b.date}
                                        {i === 0 && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">ən son</span>}
                                    </div>
                                </td>
                                <td className="px-5 py-3">
                                    {b.db ? (
                                        <button onClick={() => download(b.db.name)} disabled={!!dling}
                                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50">
                                            {dling === b.db.name ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5 text-slate-400" />}
                                            DB <span className="font-mono text-slate-400">{fmtSize(b.db.size)}</span>
                                        </button>
                                    ) : <span className="text-xs text-rose-400">yoxdur</span>}
                                </td>
                                <td className="px-5 py-3">
                                    {b.uploads ? (
                                        <button onClick={() => download(b.uploads.name)} disabled={!!dling}
                                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50">
                                            {dling === b.uploads.name ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5 text-slate-400" />}
                                            Şəkillər <span className="font-mono text-slate-400">{fmtSize(b.uploads.size)}</span>
                                        </button>
                                    ) : <span className="text-xs text-rose-400">yoxdur</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            </div>

            {/* Log */}
            {data?.log?.length > 0 && (
                <div className="mt-5 overflow-hidden rounded-2xl bg-[#0b1220]">
                    <div className="flex items-center gap-2 border-b border-white/5 px-4 py-2.5 text-xs font-medium text-slate-400">
                        <Terminal className="h-3.5 w-3.5" /> Backup jurnalı (son sətirlər)
                    </div>
                    <pre className="overflow-x-auto p-4 text-[11px] leading-relaxed text-emerald-300">{data.log.join('\n')}</pre>
                </div>
            )}

            <p className="mt-4 text-xs leading-relaxed text-slate-400">
                Bu backup-lar <b className="text-slate-600">bütün tenantları</b> əhatə edir (fəlakət bərpası üçün). Tək bir tenantı geri yükləmək
                üçün <b className="text-slate-600">Baza backup / Baza restore</b> bölmələrindən istifadə edin.
            </p>
        </SuperadminLayout>
    );
}
